import { useRef, useState, type CSSProperties, type SyntheticEvent } from "react";
import { publicIdentityBinding, usePublicIdentityMedia, type GuestIdentitySubject } from "./publicIdentityMedia";
import type { PetOrigin, PetSpecies } from "@/shared/contracts";
import { PawBadge, type AvatarSurface } from "@/shared/ui";
import { portraitChain } from "./residentView";
import { photoWidthFor, sizedPhotoSrc, usePhotoSizes } from "@/shared/ui/photoSizes";
import type { IdentityFaceCrop } from "./world/guestStoriesModel";
// 样式在 planet.css（.ps-resident-portrait）；领养页等别处也在用这个组件，这里自己引入，不靠星球页先加载。
import "./planet.css";

/** 头像不大于这个边长（px）时算小头像：“插画”小标挪到头像下沿外侧（登录 / 注册页“刚才认识的居民”是 45px）。 */
export const COMPACT_MAX = 48;

/**
 * 近景（UI-FIRST-IMPRESSION-116，用户「日常用宠物本人的近景」）：focus 是 P114 审过的头部框（world/guestStoriesModel 的 identityFaceCrop，
 * 只给官方形象 o1）。图读出来、宽高比对得上 2:3 才按框放大；对不上、换了图、没给框，都整张放进去，不自己猜框。
 * 框的位置用 CSS 变量交给 planet.css 的 .has-face-focus，页面自己的 img 尺寸规则（有的带 !important）不用跟着改。
 */
function useFaceFocus(src: string | null, focus: IdentityFaceCrop | null | undefined) {
  const [geometry, setGeometry] = useState<{ src: string; aspect: number } | null>(null);
  const crop = focus && src && geometry?.src === src && Math.abs(geometry.aspect - 2 / 3) < .001 ? focus : null;
  const onLoad = (event: SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget;
    if (src && image.isConnected && image.naturalHeight > 0) setGeometry({ src, aspect: image.naturalWidth / image.naturalHeight });
  };
  const style = crop ? ({ "--focus-w": `${100 / crop.width}%`, "--focus-h": `${100 / crop.height}%`, "--focus-x": `${-crop.x * 100}%`, "--focus-y": `${-crop.y * 100}%` } as CSSProperties) : undefined;
  return { crop, onLoad, style };
}

/**
 * 居民头像：按退回链一级一级往下（见 ./residentView 的 portraitChain，2026-09-27 D5 续）——
 * 后台设的官方形象照（`PublicResident.avatar_url`，official 为真）→ TA 原来那张照片（`fallback_avatar_url`）→ 中性爪印底
 * （10-03 起不再用同物种插画顶替，用户「图像上的插画给删除掉！」）。
 * - 图打不开（onError）就往下一级；只往下、不往回：不会在两张图之间来回闪，也不会留一张破图。数据重读、地址没变，停在已经退到的那一级；
 *   换了一位居民（地址变了）从第一级重新来。
 * - 每一级读屏说它自己的：官方形象照“{名字}的官方形象”；原来的照片按来历（原创居民“{名字}的形象（AI 生成）”，其余“{名字}的照片”）；
 *   中性底（全站同一个爪印徽章，UI-BASE-01b）：照片打不开退下来的说“{名字}，照片暂时打不开”，本来就没有照片说“{名字}，暂无照片”（全站一套说法）。
 * - fallbackUrl、official 不传（领养页、旧后端）：有照片用照片，打不开退到中性底。
 * - surface="paper"（2026-09-27 D4 第二批）：放在昼夜都是暖纸的表面上（登录 / 注册页“刚才认识的居民”）——底块、爪印用纸面那套固定色
 *   （planet.css 的 .is-on-paper），深色下不是纸上的深色块；写法同共享头像 PetAvatar、PetPortrait。不给就是原来跟主题走的样子。
 * - 小头像（size ≤ COMPACT_MAX，2026-09-27 D4 第三批）：类名多一个 is-compact（原来给“插画”小标挪位用；10-03 起没有小标了，类名照旧挂）；
 *   读屏说法不变。64px（/world）和更大的照旧。
 * - 照片缩略档（UI-PET-PHOTO-SIZES-02，10-13）：/meta 有 pets.photo_sizes 时，后端那三种照片地址按 size 带一档 w（shared/ui/photoSizes）；
 *   退回链仍按原地址认级。访客那条路（GuestResidentPortrait）经服务取 blob，同样按 size 带一档 w（UI-REVIEW110-01：访客世界页首屏原来拉 30 张原图约 24MB）。
 */
function MemberResidentPortrait({
  name,
  species,
  photoUrl,
  fallbackUrl = null,
  official = false,
  origin,
  size = 64,
  surface,
  focus = null,
}: {
  name: string;
  species: PetSpecies;
  photoUrl?: string | null;
  fallbackUrl?: string | null;
  official?: boolean;
  origin?: PetOrigin | null;
  size?: number;
  surface?: AvatarSurface;
  /** 审过的头部框（只对第一级、也就是给的那张官方形象有效） */
  focus?: IdentityFaceCrop | null;
}) {
  const mods = `${surface === "paper" ? " is-on-paper" : ""}${size <= COMPACT_MAX ? " is-compact" : ""}`;
  const sized = usePhotoSizes();
  const chain = portraitChain({ name, species, photoUrl, fallbackUrl, official, origin });
  const chainKey = chain.map((step) => (step.kind === "photo" ? step.src : "blank")).join("\n");
  const [failed, setFailed] = useState<{ chainKey: string; step: number }>({ chainKey, step: 0 });
  const at = Math.min(failed.chainKey === chainKey ? failed.step : 0, chain.length - 1);
  const current = chain[at];
  // 近景框只对第一级（给的那张）有效：退到原来那张照片后不再套框
  const face = useFaceFocus(current.kind === "photo" && at === 0 ? current.src : null, focus);
  // 只从“现在这一级”往下退一级：晚到的、别的地址的出错不会让它跳级或往回
  const next = () =>
    setFailed((prev) => {
      const now = prev.chainKey === chainKey ? prev.step : 0;
      return now === at ? { chainKey, step: at + 1 } : prev;
    });
  if (current.kind === "photo") {
    return (
      <span className={`ps-resident-portrait is-photo${mods}${face.crop ? " has-face-focus" : ""}`} style={{ width: size, height: size, ...face.style }} role="img" aria-label={current.label}>
        {/* 有近景框时图要按框放大约两倍：按两倍边长取档，免得小图放大发软（UI-FIRST-IMPRESSION-116 评审第 4 条） */}
        <img key={current.src} src={sized ? sizedPhotoSrc(current.src, focus && at === 0 ? size * 2 : size) : current.src} alt="" loading="lazy" draggable={false} onError={next} onLoad={face.onLoad} />
      </span>
    );
  }
  // 10-03 起最后一级是中性的爪印底：不画物种插画、不标“插画”（用户「图像上的插画给删除掉！」）
  return (
    <span className={`ps-resident-portrait is-blank${mods}`} style={{ width: size, height: size }} role="img" aria-label={current.label} data-species={species}>
      <PawBadge size={size} />
    </span>
  );
}

type PortraitProps = Parameters<typeof MemberResidentPortrait>[0];
export function ResidentPortrait(props: PortraitProps & { guestMedia?: GuestIdentitySubject | null }) {
  return props.guestMedia !== undefined
    ? <GuestResidentPortrait {...props} subject={props.guestMedia} />
    : <MemberResidentPortrait {...props} />;
}

function GuestResidentPortrait({ subject, name, species, photoUrl, official = false, size = 64, surface, focus = null }: PortraitProps & { subject: GuestIdentitySubject | null }) {
  const binding = publicIdentityBinding(subject, photoUrl, official);
  const sized = usePhotoSizes();
  // 有近景框时按两倍边长取档（同成员那条路）
  const media = usePublicIdentityMedia(binding, sized ? photoWidthFor(focus ? size * 2 : size) : null);
  const imageRef = useRef<HTMLImageElement>(null);
  const face = useFaceFocus(media.src, focus);
  const mods = `${surface === "paper" ? " is-on-paper" : ""}${size <= COMPACT_MAX ? " is-compact" : ""}`;
  const failure = media.failed || (!binding && Boolean(photoUrl));
  const label = media.src ? `${name}的官方形象`
    : failure ? `${name}，照片暂时打不开`
      : binding ? `${name}的官方形象正在打开` : `${name}，暂无照片`;
  return (
    <span className={`ps-resident-portrait ${media.src ? "is-photo" : "is-blank"}${mods}${face.crop ? " has-face-focus" : ""}`} style={{ width: size, height: size, ...face.style }} role="img" aria-label={label} data-species={species}>
      {media.src ? <img ref={imageRef} key={media.src} src={media.src} alt="" loading="lazy" draggable={false} onLoad={face.onLoad} onError={event => {
        if (event.currentTarget === imageRef.current && event.currentTarget.isConnected) media.fail();
      }} /> : <PawBadge size={size} />}
    </span>
  );
}

